import * as React from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { USStates } from '@gcv/shared';

import { USStateOptions } from 'domain/consts';
import { IANATimezoneOptions } from 'domain/consts/timezones';
import { useInjection } from 'ioc';
import type { ResponseAddressData } from 'ui';
import {
  Container,
  ContainerItem,
  Form,
  Header,
  InputAddress,
  InputCheckBox,
  InputSelect,
  InputText,
  InputZip
} from 'ui';
import { BusinessDetailsPresenter } from '../business-details-presenter';
import { StateChangeConfirmModal } from './state-change-confirm-modal';

interface Props {
  form: UseFormReturn<FieldValues, any>;
  isMobile: boolean;
  onChange: () => void;
}

export const AddressCard: React.FC<Props> = (props) => {
  const presenter = useInjection(BusinessDetailsPresenter);
  const [sameMailingAddress, setSameMailingAddress] = React.useState(
    !presenter.crbDispensaryStore.currentDispensary.mailingStreetAddress ||
      presenter.crbDispensaryStore.currentDispensary.streetAddress ===
        presenter.crbDispensaryStore.currentDispensary.mailingStreetAddress
  );
  const [showConfirmStateChangeModal, setShowConfirmStateChangeModal] = React.useState(false);
  const [stateChangeData, setStateChangeData] = React.useState({
    newStateName: '',
    newState: '',
    stateFieldName: '',
    selectedAddress: {} as ResponseAddressData
  });

  const handleMailingAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSameMailingAddress(event.target.checked);
    props.onChange();
  };

  const onStateChange = (stateFieldName: string, selectedAddress: ResponseAddressData) => {
    const state = USStates[selectedAddress.Place.Region.replace(' ', '_') as keyof typeof USStates];
    if (stateChangeData.newState === '' && state !== props.form.getValues(stateFieldName)) {
      setStateChangeData({
        newStateName: selectedAddress.Place.Region,
        newState: state,
        stateFieldName: stateFieldName,
        selectedAddress: selectedAddress
      });
      setShowConfirmStateChangeModal(true);
    } else {
      props.form.setValue(stateFieldName, state);
      props.form.setValue('city', selectedAddress.Place.Municipality);
      props.form.setValue('postalCode', selectedAddress.Place.PostalCode);
      props.form.setValue('iana_timezone', selectedAddress.Place.TimeZone.Name);
      props.onChange();
    }
  };

  return (
    <>
      <Container padding="28px 0 0 0" dataCy="address-card">
        <Container padding="0 0 8px 0">
          <Header mobileHeader={props.isMobile} content="Registered Entity Address" type="h2" />
        </Container>
        <Form>
          <Container column={props.isMobile} padding={0} width="100%">
            <ContainerItem
              padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
              width="100%"
              maxWidth={props.isMobile ? 'none' : '20%'}
              minWidth={props.isMobile ? 'none' : '300px'}
            >
              <InputAddress
                name="streetAddress"
                dataCy="street-address"
                label="Address"
                onChange={(selectedAddress: ResponseAddressData) => {
                  onStateChange('state', selectedAddress);
                }}
                onBlur={() => {
                  if (!showConfirmStateChangeModal) props.onChange();
                }}
                rules={{
                  required: { message: 'is required', value: true }
                }}
                form={props.form}
                {...props.form}
              />
            </ContainerItem>
            <ContainerItem
              padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
              width="100%"
              maxWidth={props.isMobile ? 'none' : '20%'}
              minWidth={props.isMobile ? 'none' : '210px'}
            >
              <InputText
                name="city"
                label="City"
                rules={{
                  required: { message: 'is required', value: true },
                  pattern: { message: 'must be valid', value: /^[a-zA-Z ]+$/ }
                }}
                dataCy="city"
                onChange={props.onChange}
                onBlur={props.onChange}
                {...props.form}
              />
            </ContainerItem>
            <ContainerItem
              padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
              width="100%"
              maxWidth={props.isMobile ? 'none' : '20%'}
              minWidth={props.isMobile ? 'none' : '210px'}
            >
              <InputSelect
                {...props.form}
                dataCy="state"
                label="State"
                name="state"
                options={USStateOptions}
                rules={{ required: { message: 'is required', value: true } }}
                additionalOnChange={props.onChange}
                onBlur={props.onChange}
              />
            </ContainerItem>
            <ContainerItem
              padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
              width="100%"
              maxWidth={props.isMobile ? 'none' : '20%'}
              minWidth={props.isMobile ? 'none' : '210px'}
            >
              <InputZip
                name="postalCode"
                placeholder=" " // added a whitespace to remove the placeholder instead of adding a additional prop
                label="Zip Code"
                rules={{
                  required: { message: 'is required', value: true }
                }}
                dataCy="postal-code"
                onChange={props.onChange}
                onBlur={props.onChange}
                {...props.form}
              />
            </ContainerItem>
            <ContainerItem
              padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
              width="100%"
              maxWidth={props.isMobile ? 'none' : '20%'}
              minWidth={props.isMobile ? 'none' : '210px'}
            >
              <InputSelect
                name="iana_timezone"
                label="Timezone"
                options={IANATimezoneOptions}
                rules={{
                  required: { message: 'is required', value: true }
                }}
                dataCy="timezone"
                additionalOnChange={props.onChange}
                onBlur={props.onChange}
                {...props.form}
              />
            </ContainerItem>
          </Container>
        </Form>
        <Form>
          <Container padding="0" width="100%">
            <ContainerItem padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}>
              <InputCheckBox
                label="Mailing Address same as above"
                name="sameMailingAddress"
                onChange={handleMailingAddress}
                dataCy="same-mailing-address-checkbox"
                {...props.form}
              />
            </ContainerItem>
          </Container>
        </Form>

        <Form>
          <Container padding={0} width="100%">
            {(!sameMailingAddress ||
              (!sameMailingAddress &&
                presenter.crbDispensaryStore.currentDispensary.mailingStreetAddress)) && (
              <>
                <ContainerItem
                  padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
                  width="100%"
                  maxWidth={props.isMobile ? 'none' : '20%'}
                  minWidth={props.isMobile ? 'none' : '300px'}
                >
                  <InputAddress
                    name="mailingStreetAddress"
                    dataCy="mailing-street-address"
                    label="Mailing Address"
                    onChange={(selectedAddress: ResponseAddressData) => {
                      const state =
                        USStates[selectedAddress.Place.Region.replace(' ', '_') as keyof typeof USStates];
                      props.form.setValue('mailingState', state);
                      props.form.setValue('mailingCity', selectedAddress.Place.Municipality);
                      props.form.setValue('mailingPostalCode', selectedAddress.Place.PostalCode);
                      props.onChange();
                    }}
                    onBlur={() => {
                      props.onChange();
                    }}
                    rules={{
                      required: { message: 'is required', value: true }
                    }}
                    form={props.form}
                    {...props.form}
                  />
                </ContainerItem>
                <ContainerItem
                  padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
                  width="100%"
                  maxWidth={props.isMobile ? 'none' : '20%'}
                  minWidth={props.isMobile ? 'none' : '210px'}
                >
                  <InputText
                    name="mailingCity"
                    label="Mailing City"
                    rules={{
                      required: { message: 'is required', value: true },
                      pattern: { message: 'must be valid', value: /^[a-zA-Z ]+$/ }
                    }}
                    dataCy="mailing-city"
                    onChange={props.onChange}
                    onBlur={props.onChange}
                    {...props.form}
                  />
                </ContainerItem>
                <ContainerItem
                  padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
                  width="100%"
                  maxWidth={props.isMobile ? 'none' : '20%'}
                  minWidth={props.isMobile ? 'none' : '210px'}
                >
                  <InputSelect
                    name="mailingState"
                    label="Mailing State"
                    options={USStateOptions}
                    rules={{ required: { message: 'is required', value: true } }}
                    dataCy="mailing-state"
                    additionalOnChange={props.onChange}
                    onBlur={props.onChange}
                    {...props.form}
                  />
                </ContainerItem>
                <ContainerItem
                  padding={props.isMobile ? '10px 0' : '10px 12px 10px 0'}
                  width="100%"
                  maxWidth={props.isMobile ? 'none' : '20%'}
                  minWidth={props.isMobile ? 'none' : '210px'}
                >
                  <InputZip
                    name="mailingPostalCode"
                    placeholder=" "
                    label="Mailing Zip Code"
                    rules={{
                      required: { message: 'is required', value: true }
                    }}
                    dataCy="mailing-postal-code"
                    onChange={props.onChange}
                    onBlur={props.onChange}
                    {...props.form}
                  />
                </ContainerItem>
              </>
            )}
          </Container>
        </Form>
      </Container>
      <StateChangeConfirmModal
        isOpen={showConfirmStateChangeModal}
        onClose={() => {
          setShowConfirmStateChangeModal(false);
          setStateChangeData({
            newStateName: '',
            newState: '',
            stateFieldName: '',
            selectedAddress: {} as ResponseAddressData
          });
        }}
        onConfirm={() => {
          props.form.setValue(stateChangeData.stateFieldName, stateChangeData.newState);
          props.form.setValue('city', stateChangeData.selectedAddress.Place.Municipality);
          props.form.setValue('postalCode', stateChangeData.selectedAddress.Place.PostalCode);
          props.form.setValue('iana_timezone', stateChangeData.selectedAddress.Place.TimeZone.Name);
          props.onChange();
          setShowConfirmStateChangeModal(false);
        }}
        newState={stateChangeData.newStateName}
      />
    </>
  );
};
